
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { generateCharacterSlug } from "@/utils/characterSlug";
import { Eye } from "lucide-react";
import { useEnhancedColorThemes } from "@/hooks/useEnhancedColorThemes";
import { getStorageUrl, STORAGE_BUCKETS } from "@/utils/storageUtils";

interface Character {
  id: string;
  name: string;
  species: string;
  role: string;
  description: string;
  personality: string;
  teaching_focus: string;
  special_talents: string;
  fun_facts: any;
  image_url: string;
  avatar_url: string;
  color_theme: string;
  age_group: string;
  featured: boolean;
}

interface CharacterCardProps {
  character: Character;
}

export const CharacterCard = ({ character }: CharacterCardProps) => {
  const { getEnhancedTheme } = useEnhancedColorThemes();
  const funFactsList = character.fun_facts && Array.isArray(character.fun_facts) 
    ? character.fun_facts 
    : [];

  const enhancedTheme = getEnhancedTheme(character.color_theme);
  const characterSlug = generateCharacterSlug(character.name);

  // Get the image URL, handling both storage paths and external URLs
  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http')) return imagePath;
    return getStorageUrl(STORAGE_BUCKETS.CHARACTER_IMAGES, imagePath);
  };

  const avatarUrl = getImageUrl(character.avatar_url);
  const imageUrl = getImageUrl(character.image_url);

  return (
    <Link to={`/characters/${characterSlug}`} className="block h-full">
      <Card className="hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden h-full flex flex-col cursor-pointer">
        <CardHeader 
          className="p-0 h-32 relative"
          style={{
            background: `linear-gradient(to right, ${enhancedTheme.gradientColors.to}, ${enhancedTheme.gradientColors.from})`
          }}
        >
          {character.featured && (
            <Badge className="absolute top-2 right-2 bg-yellow-500 text-white">
              Featured
            </Badge>
          )}
          <div className="absolute inset-0 bg-black/20"></div>
        </CardHeader>
        
        <CardContent className="p-6 relative flex flex-col flex-1">
          <div className="flex flex-col items-center -mt-16 mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden shadow-lg bg-white">
              {avatarUrl || imageUrl ? (
                <img
                  src={avatarUrl || imageUrl}
                  alt={character.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-600">
                    {character.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="text-center space-y-3 flex-1 flex flex-col">
            <h3 className="text-xl font-bold text-teedee-blue">{character.name}</h3>
            
            <div className="flex justify-center gap-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {character.species}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {character.role}
              </Badge>
            </div>

            {character.description && (
              <p className="text-sm text-gray-600 line-clamp-3">
                {character.description}
              </p>
            )}

            <div className="space-y-3 flex-1">
              {character.teaching_focus && (
                <div className="bg-sky-blue/10 rounded-lg p-3">
                  <p className="text-xs font-medium text-teedee-blue mb-1">Teaching Focus:</p>
                  <p className="text-sm text-gray-700">{character.teaching_focus}</p>
                </div>
              )}

              {character.personality && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 mb-1">Personality:</p>
                  <p className="text-sm text-gray-700">{character.personality}</p>
                </div>
              )}

              {character.special_talents && (
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-orange-700 mb-1">Special Talents:</p>
                  <p className="text-sm text-gray-700">{character.special_talents}</p>
                </div>
              )}

              {funFactsList.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-purple-700 mb-2">Fun Facts:</p>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {funFactsList.slice(0, 2).map((fact: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-500 mr-1">•</span>
                        {fact}
                      </li>
                    ))}
                    {funFactsList.length > 2 && (
                      <li className="text-xs text-gray-500 italic">
                        + {funFactsList.length - 2} more facts...
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {character.age_group && (
                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                  Ages: {character.age_group}
                </Badge>
              )}
            </div>

            {/* View Details Button */}
            <div className="pt-4 mt-auto">
              <Button className="w-full group pointer-events-none">
                <Eye className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                View Details
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
